<script setup>
import { ref, unref, provide, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import ChatListHeader from './ChatListHeader.vue';
import ConversationPhoneSearch from './ConversationPhoneSearch.vue';
import ConversationsAPI from 'dashboard/api/conversations';
import ConversationList from './ConversationList.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useConversationListResize } from 'dashboard/composables/chatlist/useConversationListResize';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useConversationDeleteUndo } from 'dashboard/composables/useConversationDeleteUndo';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import {
  sortPinnedConversationsFirst,
  usePinnedConversations,
} from 'dashboard/composables/chatlist/usePinnedConversations';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import { sortByUnreadStatus } from '../store/modules/conversations/helpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings, updateUISettings } = useUISettings();
const listPanel = ref(null);
const listResize = useConversationListResize(
  listPanel,
  uiSettings,
  updateUISettings
);
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { offerUndo: offerDeleteUndo, offerBulkUndo } =
  useConversationDeleteUndo();

const resolveAttributesModalRef = ref(null);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
const phoneInput = ref('');
const phoneSearchOpen = ref(false);
const phoneSearchBaseFilters = ref(null);
// True while a contact search (or its restore) waits for the server response.
const contactSearchPending = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const isContactSearchRefreshing = computed(
  () => contactSearchPending.value && chatListLoading.value
);
watch(chatListLoading, loading => {
  if (!loading) contactSearchPending.value = false;
});
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');
const selectedChat = useMapGetter('getSelectedChat');
const selectedChatUnreadCount = useMapGetter('getSelectedChatUnreadCount');

const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();
const {
  pinnedConversationIds,
  isConversationPinned,
  togglePinnedConversation,
} = usePinnedConversations();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const getContact = useMapGetter('contacts/getContact');
const folderContactId = useMapGetter('customViews/getActiveFolderContactId');

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const resolvedStatus = computed(() => {
  if (activeStatus.value === wootConstants.STATUS_TYPE.OPEN_AND_PENDING) {
    return ['open', 'pending'];
  }
  return activeStatus.value;
});

// "Todos os atendimentos": sem caixa, time, etiqueta, pasta ou tipo de conversa.
const isAllConversationsView = computed(
  () =>
    !props.conversationInbox &&
    !props.teamId &&
    !props.label &&
    !props.conversationType &&
    !props.foldersId
);
const hasEmailInbox = computed(() =>
  (inboxesList.value || []).some(item => item.channel_type === 'Channel::Email')
);
const canHideEmails = computed(
  () => isAllConversationsView.value && hasEmailInbox.value
);
const excludeEmail = computed(
  () =>
    canHideEmails.value &&
    uiSettings.value?.conversation_list_hide_emails === true
);

const conversationFilters = computed(() => {
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: resolvedStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    excludeEmail: excludeEmail.value || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.AI) {
    return 'Atendimento IA';
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.EMAIL) {
    return t('SIDEBAR.EMAIL_CONVERSATIONS');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.RESOLVED) {
    return t('SIDEBAR.RESOLVED_CONVERSATIONS');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

function filterByAssigneeTab(conversations) {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return conversations.filter(
      c => c.meta?.assignee?.id === currentUser.value?.id
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return conversations.filter(c => !c.meta?.assignee);
  }
  return [...conversations];
}

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  if (
    !hasAppliedFiltersOrActiveFolders.value &&
    activeSortBy.value === wootConstants.SORT_BY_TYPE.UNREAD
  ) {
    localConversationList = sortByUnreadStatus(localConversationList, {
      selectedChatId: selectedChat.value.id,
      selectedChatUnreadCount: selectedChatUnreadCount.value,
    });
  }

  return sortPinnedConversationsFirst(
    localConversationList,
    pinnedConversationIds.value
  );
});

const showEndOfListMessage = computed(() => {
  return !!(
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value =
    props.conversationType === wootConstants.CONVERSATION_TYPE.RESOLVED
      ? wootConstants.STATUS_TYPE.RESOLVED
      : status || wootConstants.STATUS_TYPE.ALL;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload, replace = false) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
      replace,
    })
    .catch(() => useAlert(t('CHAT_LIST.FETCH_ERROR')))
    // emit even on failure so a deep-linked conversation still loads via
    // fetchConversationIfUnavailable
    .finally(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .catch(() => useAlert(t('CHAT_LIST.FETCH_ERROR')))
    .finally(emitConversationLoaded);
}

function onApplyFilter(payload, preserveList = false) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  if (!preserveList) store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload, preserveList);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    contacts: [getContact.value(folderContactId.value)],
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations(replace = false) {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store
    .dispatch('fetchAllConversations', { replace })
    .then(emitConversationLoaded);
}

function resetAndFetchData(preserveList = false) {
  contactSearchPending.value = preserveList;
  phoneSearchBaseFilters.value = null;
  phoneInput.value = '';
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  if (!preserveList) store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations(preserveList);
}

function onPhoneSearch(term) {
  if (!term && phoneSearchBaseFilters.value === null) return;
  contactSearchPending.value = true;
  if (!term) {
    const previousFilters = phoneSearchBaseFilters.value;
    phoneSearchBaseFilters.value = null;
    if (!previousFilters.length) {
      const currentInput = phoneInput.value;
      resetAndFetchData(true);
      phoneInput.value = currentInput;
    } else {
      store.dispatch('setConversationFilters', useSnakeCase(previousFilters));
      onApplyFilter(previousFilters, true);
    }
    return;
  }

  if (phoneSearchBaseFilters.value === null) {
    phoneSearchBaseFilters.value = JSON.parse(
      JSON.stringify(appliedFilters.value)
    );
  }
  appliedFilter.value = [];
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  } else if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  } else {
    initializeExistingFilterToModal();
  }
  const filters = appliedFilter.value.filter(
    filter => filter.attributeKey !== 'contact_search'
  );
  filters.push({
    attributeKey: 'contact_search',
    attributeModel: 'standard',
    filterOperator: 'contains',
    values: term,
    queryOperator: 'and',
  });
  store.dispatch('setConversationFilters', useSnakeCase(filters));
  onApplyFilter(filters, true);
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  } else if (props.conversationType) {
    conversationType = props.conversationType;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

const clearConversationDialogRef = ref(null);
const conversationToClear = ref(null);
const isClearingConversation = ref(false);

async function clearConversation() {
  if (isClearingConversation.value || !conversationToClear.value) return;
  isClearingConversation.value = true;
  try {
    const { data } = await ConversationsAPI.clear(conversationToClear.value);
    await store.dispatch('updateConversation', data);
    clearConversationDialogRef.value.close();
    conversationToClear.value = null;
    useAlert(t('CONVERSATION.CLEAR_CONVERSATION.SUCCESS'));
  } catch {
    useAlert(t('CONVERSATION.CLEAR_CONVERSATION.ERROR'));
  } finally {
    isClearingConversation.value = false;
  }
}

function handleClear(conversationId) {
  conversationToClear.value = conversationId;
  clearConversationDialogRef.value.open();
}

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);
const resolveAllDialogRef = ref(null);

async function resolveAllOpenConversations() {
  try {
    const params = {
      inbox_id: props.conversationInbox || undefined,
      team_id: props.teamId || undefined,
      labels: props.label ? [props.label] : undefined,
      assignee_type: activeAssigneeTab.value,
    };
    const { data } = await ConversationsAPI.resolveAllOpen(params);
    resolveAllDialogRef.value?.close();
    useAlert(t('BULK_ACTION.RESOLVE_ALL_SUCCESS', { count: data.count }));
    resetAndFetchData();
  } catch {
    useAlert(t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
  }
}

async function deleteConversation() {
  const conversationId = selectedConversationId.value;
  const startedAt = Date.now();
  try {
    const result = await store.dispatch('deleteConversation', conversationId);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    offerDeleteUndo({
      conversationId,
      undoToken: result?.undo_token,
      windowSeconds: result?.undo_window_seconds,
      elapsedMs: Date.now() - startedAt,
    });
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const bulkDeleteDialogRef = ref(null);
const bulkDeleteCount = ref(0);
const isBulkDeleting = ref(false);

function openBulkDeleteDialog() {
  bulkDeleteCount.value = selectedConversations.value.length;
  bulkDeleteDialogRef.value?.open();
}

// Exclui as conversas selecionadas com a mesma janela de desfazer da exclusão individual.
async function deleteSelectedConversations() {
  const conversationIds = [...selectedConversations.value];
  if (!conversationIds.length || isBulkDeleting.value) return;

  isBulkDeleting.value = true;
  const startedAt = Date.now();
  try {
    const result = await store.dispatch(
      'bulkDeleteConversations',
      conversationIds
    );
    if (conversationIds.includes(selectedChat.value?.id)) {
      redirectToConversationList();
    }
    resetBulkActions();
    bulkDeleteDialogRef.value?.close();
    offerBulkUndo({
      conversations: result?.conversations || [],
      windowSeconds: result?.undo_window_seconds,
      elapsedMs: Date.now() - startedAt,
    });
  } catch {
    useAlert(t('BULK_ACTION.DELETE.FAILED'));
  } finally {
    isBulkDeleting.value = false;
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);
provide('clearConversation', handleClear);
provide('isConversationPinned', isConversationPinned);
provide('togglePinnedConversation', togglePinnedConversation);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => {
    setFiltersFromUISettings();
    resetAndFetchData();
  }
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

// Ligar/desligar "Ocultar e-mails" (ou as caixas carregarem depois) refaz a lista.
watch(
  () => [
    uiSettings.value?.conversation_list_hide_emails === true,
    hasEmailInbox.value,
  ],
  ([hide, hasEmail], [prevHide, prevHasEmail]) => {
    if (!isAllConversationsView.value || hasAppliedFilters.value) return;
    if (!hide && !prevHide) return;
    if (hide === prevHide && hasEmail === prevHasEmail) return;
    resetAndFetchData(true);
  }
);
</script>

<template>
  <div
    ref="listPanel"
    class="flex min-w-0 flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1 relative"
    :style="{ '--conversation-list-width': `${listResize.width.value}px` }"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout
        ? 'basis-full'
        : 'w-full md:w-[var(--conversation-list-width)]',
    ]"
  >
    <div
      v-if="!isOnExpandedLayout"
      role="separator"
      tabindex="0"
      aria-orientation="vertical"
      :aria-label="$t('CHAT_LIST.RESIZE_LABEL')"
      :title="$t('CHAT_LIST.RESIZE_HINT')"
      :aria-valuemin="listResize.minWidth"
      :aria-valuemax="listResize.maxWidth.value"
      :aria-valuenow="listResize.width.value"
      class="group absolute inset-y-0 end-0 z-30 hidden w-2 translate-x-1/2 cursor-col-resize touch-none items-center justify-center focus-visible:outline-none md:flex rtl:-translate-x-1/2"
      @pointerdown="listResize.startResize"
      @keydown="listResize.onKeydown"
      @dblclick="listResize.resetWidth"
    >
      <span
        class="h-full w-0.5 rounded-full transition-colors group-hover:bg-n-brand/50 group-focus-visible:bg-n-brand"
        :class="listResize.isResizing.value && 'bg-n-brand'"
      />
      <span
        class="absolute h-9 w-1 rounded-full bg-n-slate-6 group-hover:bg-n-brand group-focus-visible:bg-n-brand"
      />
    </div>
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :phone-search-open="phoneSearchOpen"
      :has-phone-search="phoneSearchBaseFilters !== null"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :show-email-sync="
        conversationType === wootConstants.CONVERSATION_TYPE.EMAIL
      "
      :email-sync-inbox-id="conversationInbox || null"
      :show-hide-emails="canHideEmails"
      @toggle-phone-search="phoneSearchOpen = !phoneSearchOpen"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @resolve-all="resolveAllDialogRef?.open()"
    />

    <ConversationPhoneSearch
      v-model="phoneInput"
      :open="phoneSearchOpen"
      :searching="isContactSearchRefreshing"
      @search="onPhoneSearch"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />

    <p
      v-if="!chatListLoading && !conversationList.length"
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      :class="isOnExpandedLayout && 'sm:!w-[24rem] !w-full'"
      @select-all-conversations="toggleSelectAll"
      @delete-selected="openBulkDeleteDialog"
    />
    <ConversationList
      :conversation-list="conversationList"
      :pinned-conversation-ids="pinnedConversationIds"
      :is-loading="chatListLoading"
      :hide-loading-indicator="
        phoneSearchBaseFilters !== null || isContactSearchRefreshing
      "
      :is-refreshing="isContactSearchRefreshing"
      :show-end-of-list-message="showEndOfListMessage"
      :label="label"
      :team-id="teamId"
      :folders-id="foldersId"
      :conversation-type="conversationType"
      :show-assignee="showAssigneeInConversationCard"
      :is-on-expanded-layout="isOnExpandedLayout"
      @load-more="loadMoreConversations"
    />
    <Dialog
      ref="clearConversationDialogRef"
      type="alert"
      :title="$t('CONVERSATION.CLEAR_CONVERSATION.TITLE')"
      :description="$t('CONVERSATION.CLEAR_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.CLEAR_CONVERSATION.CONFIRM')"
      :is-loading="isClearingConversation"
      :disable-confirm-button="isClearingConversation"
      :close-on-click-outside="!isClearingConversation"
      @confirm="clearConversation"
    />
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <Dialog
      ref="bulkDeleteDialogRef"
      type="alert"
      :title="
        $t(
          'BULK_ACTION.DELETE.TITLE',
          { count: bulkDeleteCount },
          bulkDeleteCount
        )
      "
      :description="$t('BULK_ACTION.DELETE.DESCRIPTION')"
      :confirm-button-label="$t('BULK_ACTION.DELETE.CONFIRM')"
      :is-loading="isBulkDeleting"
      @confirm="deleteSelectedConversations"
    />
    <Dialog
      ref="resolveAllDialogRef"
      type="alert"
      :title="$t('BULK_ACTION.RESOLVE_ALL_TITLE')"
      :description="$t('BULK_ACTION.RESOLVE_ALL_DESCRIPTION')"
      :confirm-button-label="$t('BULK_ACTION.RESOLVE_ALL_CONFIRM')"
      @confirm="resolveAllOpenConversations"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
